import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, Switch } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { PeriodDateRangeFields, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';

type Row = {
  departmentId: number;
  departmentCode: string;
  departmentName: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  net: number;
};

type Response = {
  rows: Row[];
  totals: { debit: number; credit: number; net: number };
};

export const DepartmentAnalysis: React.FC = () => {
  const { colors } = useTheme();
  const f = useAccountingReportFilters();
  const [useLocalCurrency, setUseLocalCurrency] = useState(true);

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/department-analysis', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          useLocalCurrency,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      { key: 'departmentCode', header: 'Dept', sortable: true },
      { key: 'departmentName', header: 'Department' },
      { key: 'accountCode', header: 'Account' },
      { key: 'accountName', header: 'Account name' },
      { key: 'debit', header: 'Debit', align: 'right', render: formatReportAmount },
      { key: 'credit', header: 'Credit', align: 'right', render: formatReportAmount },
      {
        key: 'net',
        header: 'Net',
        align: 'right',
        render: formatReportAmount,
        footerType: 'custom',
        footerRender: () => (data ? formatReportAmount(data.totals.net) : null),
      },
    ],
    [data],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
        <Switch label="Local currency" checked={useLocalCurrency} onChange={(e) => { setUseLocalCurrency(e.target.checked); f.setRun(false); }} size="sm" />
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
          {f.companyScopeLabel}
        </Badge>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Department Analysis"
      exportFileBase="department-analysis"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      statusExtra={
        <Badge variant="secondary" className="font-normal">
          {useLocalCurrency ? 'Local currency' : 'USD'}
        </Badge>
      }
      recordCount={rows.length}
      readyMessage="Choose period and click Search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => `${r.departmentId}-${r.accountCode}`}
        showFooter
        stickyHeader
        emptyMessage="No department postings in period."
      />
    </AccountingReportShell>
  );
};
